import { lazy, Suspense } from 'react';
import { Mail, MapPin, Phone } from 'lucide-react';
import type React from 'react';

import { Container } from '~/components/ui/Container';
import { Reveal } from '~/components/ui/Reveal';
import { SectionHeader } from '~/components/ui/SectionHeader';
import { cn } from '~/lib/cn';
import { useContent } from '~/hooks/useContent';

// Lazy-load the form chunk — it includes react-hook-form + zod (~60-80 KB).
// The form is far below the fold so it never blocks initial render.
const ContactForm = lazy(() => import('~/components/forms/ContactForm'));

/**
 * Skeleton placeholder that matches the approximate height of the ContactForm
 * while the lazy chunk loads. Prevents layout shift.
 */
function ContactFormSkeleton(): React.ReactElement {
  return (
    <div
      aria-hidden="true"
      className={cn(
        'flex flex-col gap-5',
        // Approximate form height so the layout doesn't shift when the form mounts
        'min-h-[540px]'
      )}
    >
      {/* Five field skeletons */}
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex flex-col gap-1.5">
          <div className="h-3 w-24 rounded-sm bg-[rgba(232,236,244,0.08)] animate-pulse" />
          <div className="h-10 w-full rounded-sm bg-[rgba(232,236,244,0.05)] animate-pulse" />
        </div>
      ))}
      {/* Submit button skeleton */}
      <div className="h-14 w-full rounded-sm bg-[rgba(232,236,244,0.08)] animate-pulse mt-1" />
    </div>
  );
}

/**
 * Contact — two-column layout (details left, RFQ form right) on desktop; stacked on mobile.
 * id="contact" is used for nav scroll-spy and anchor links.
 */
function Contact(): React.ReactElement {
  const content = useContent();
  const { contact } = content;

  return (
    <section
      id="contact"
      className={cn('bg-[var(--color-deep-panel)]', 'py-24 md:py-32', 'scroll-anchor')}
      aria-labelledby="contact-heading"
    >
      <Container>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* Left column — section header + contact details */}
          <Reveal>
            <div className="flex flex-col gap-10">
              <SectionHeader
                eyebrow={contact.eyebrow}
                heading={contact.heading}
                lead={contact.lead}
                align="left"
                id="contact-heading"
              />

              <address className="not-italic flex flex-col gap-5">
                {/* Postal address — structured multi-line for B2B credibility */}
                <div className="flex items-start gap-3">
                  <MapPin
                    size={18}
                    strokeWidth={1.5}
                    aria-hidden="true"
                    className="flex-shrink-0 mt-0.5 text-[var(--color-cyan-signal)]"
                  />
                  <span
                    className={cn(
                      'font-body text-base text-[var(--color-steel-mist)] leading-[1.7]',
                      'flex flex-col'
                    )}
                  >
                    <span className="text-[var(--color-frost-white)]">
                      {contact.details.companyName}
                    </span>
                    <span>{contact.details.street}</span>
                    <span>
                      {contact.details.postalCode} {contact.details.city}
                    </span>
                    <span>{contact.details.country}</span>
                  </span>
                </div>

                {/* Phone */}
                <div className="flex items-center gap-3">
                  <Phone
                    size={18}
                    strokeWidth={1.5}
                    aria-hidden="true"
                    className="flex-shrink-0 text-[var(--color-cyan-signal)]"
                  />
                  <a
                    href={`tel:${contact.details.phone.replace(/\s/g, '')}`}
                    className={cn(
                      'font-body text-base text-[var(--color-steel-mist)]',
                      'hover:text-[var(--color-frost-white)] transition-colors duration-150'
                    )}
                  >
                    {contact.details.phone}
                  </a>
                </div>

                {/* Email */}
                <div className="flex items-center gap-3">
                  <Mail
                    size={18}
                    strokeWidth={1.5}
                    aria-hidden="true"
                    className="flex-shrink-0 text-[var(--color-cyan-signal)]"
                  />
                  <a
                    href={`mailto:${contact.details.email}`}
                    className={cn(
                      'font-body text-base text-[var(--color-steel-mist)]',
                      'hover:text-[var(--color-frost-white)] transition-colors duration-150'
                    )}
                  >
                    {contact.details.email}
                  </a>
                </div>

                {/* Polish legal registration numbers — required for B2B disclosure */}
                <dl
                  className={cn(
                    'mt-2 pt-5 border-t border-[rgba(232,236,244,0.08)]',
                    'grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5',
                    'font-body text-sm text-[var(--color-steel-mist)]'
                  )}
                >
                  <dt className="font-display font-semibold tracking-[0.12em] uppercase text-xs text-[var(--color-frost-white)]/70">
                    {contact.details.nipLabel}
                  </dt>
                  <dd className="font-mono">{contact.details.nip}</dd>
                  <dt className="font-display font-semibold tracking-[0.12em] uppercase text-xs text-[var(--color-frost-white)]/70">
                    {contact.details.krsLabel}
                  </dt>
                  <dd className="font-mono">{contact.details.krs}</dd>
                  <dt className="font-display font-semibold tracking-[0.12em] uppercase text-xs text-[var(--color-frost-white)]/70">
                    {contact.details.regonLabel}
                  </dt>
                  <dd className="font-mono">{contact.details.regon}</dd>
                </dl>
              </address>
            </div>
          </Reveal>

          {/* Right column — RFQ form (lazy-loaded; skeleton shown while chunk loads) */}
          <Reveal delay={120}>
            <Suspense fallback={<ContactFormSkeleton />}>
              <ContactForm />
            </Suspense>
          </Reveal>
        </div>
      </Container>
    </section>
  );
}

export { Contact };
export default Contact;
